import { useAppDispatch, useAppSelector } from "../../../services/store";
import {
  fetchEyengagePosts, fetchEymanSettings, getCurrentChild, getCurrentNursery,
  getEyengageFilterCategories,
  getParentPermissionOnEyengage,
  getPostsCurrentPage,
  getPostsData, getPostsLastPage, getPostsLoading,
  getPostsPaginatedLoading,
  getSelectedCategoriesFilter,
  getSelectedPostTypeFilter,
  updateEyengageFilterApplied,
  updateSelectedPostCategoryFilter,
  updateSelectedPostTypeFilter,
} from "src/shared/redux-state/src";
import { useCallback, useEffect, useRef } from "react";
import { ChildrenStoreData, NurseryGroupStoreData } from "src/shared/types/src";
import { BaseCard } from "src/app/components";
import PostContainer from "./PostContainer";
import { MediaFiveLoader, MediaTwoLoader, MediaOneLoader, MediaThreeLoader } from "src/app/components";
import LocalImages from './../../../static';
import { useLocation } from "react-router-dom";
import { EYENGAGE_URL_CONSTANTS } from "src/app/config/appConstants";
import { getParamsFromQuery } from "src/app/utilities/helpers";


const FeedContainer = () => {
  const dispatch = useAppDispatch();
  const location = useLocation(); // Get the current location
  const eyengagePosts = useAppSelector(getPostsData);
  const postsLoading = useAppSelector(getPostsLoading);
  const paginatedLoading = useAppSelector(getPostsPaginatedLoading);
  const currentPage = useAppSelector(getPostsCurrentPage);
  const lastPage = useAppSelector(getPostsLastPage);
  const currentNursery: NurseryGroupStoreData = useAppSelector(getCurrentNursery);
  const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);
  const selectedTypeFilter = useAppSelector(getSelectedPostTypeFilter);
  const selectedCategories = useAppSelector(getSelectedCategoriesFilter);
  const parentPermission = useAppSelector(getParentPermissionOnEyengage);
  const observer = useRef<IntersectionObserver | null>(null);
  const filterCategories = useAppSelector(getEyengageFilterCategories);

  const fetchPosts = useCallback(async (page: number, type?: string, categories?: string) => {
    if (!page) page = 1;
    await dispatch(fetchEyengagePosts({ child: currentChild, page, type, categories }));
  }, [dispatch, currentChild]);

  useEffect(() => {
    if (Boolean(currentChild?.systemsEnabled?.eyengage))
      dispatch(fetchEymanSettings({ child: currentChild }));
  }, [currentChild.childInstanceKey]);

  useEffect(() => {
    if (currentChild.systemsEnabled?.eyengage) {
      const params = getParamsFromQuery()
      const filterType = params?.get(EYENGAGE_URL_CONSTANTS.TYPE);
      const filterCategory = params?.get(EYENGAGE_URL_CONSTANTS.CATEGORIES);

      fetchPosts(1, filterType || '', filterCategory || '');

      const postFilter = filterType?.split(',') || [];
      dispatch(updateSelectedPostTypeFilter({ postFilter }));

      const filtersCategoryArray = filterCategory?.split(',') || [];
      const categoryFilter = filterCategories.filter(val => filtersCategoryArray.includes(val.id.toString()));
      dispatch(updateSelectedPostCategoryFilter({ categoryFilter }));

      if (postFilter?.length > 0 || categoryFilter?.length > 0) {
        dispatch(updateEyengageFilterApplied({ filterApplied: true }));
      } else {
        dispatch(updateEyengageFilterApplied({ filterApplied: false }));
      }
    }
  }, [dispatch, currentChild.systemsEnabled?.eyengage, currentNursery.nurseryInstanceKey, location.search]);

  const fetchNextPage = useCallback(async () => {

    if (postsLoading || paginatedLoading) return;
    if ((currentPage === lastPage) || paginatedLoading) return;
    await fetchPosts(currentPage + 1, selectedTypeFilter.join(',') ?? undefined, selectedCategories?.map((item: any) => item.id)?.join(',') ?? undefined);

  }, [postsLoading, paginatedLoading, currentPage, lastPage, fetchPosts, selectedTypeFilter, selectedCategories]);


  // Intersection Observer to trigger fetchNextPage
  useEffect(() => {
    const options = {
      root: null, // Use the viewport as the root
      rootMargin: '0px',
      threshold: 0.1 // Trigger when 10% of the element is visible
    };

    observer.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = Number(entry.target.getAttribute('data-index'));
          // Check if it's the 5th last element
          if (index > (eyengagePosts.length - 5)) {
            fetchNextPage();
          }
        }
      });
    }, options);

    const currentPosts = document.querySelectorAll('.post-item'); // Assuming each PostContainer has a class of 'post-item'
    currentPosts.forEach((post, index) => {
      post.setAttribute('data-index', index.toString());
      observer.current?.observe(post);
    });

    return () => {
      if (observer.current) {
        currentPosts.forEach(post => {
          observer.current?.unobserve(post);
        });
      }
    };
  }, [eyengagePosts, fetchNextPage]);

  if (!postsLoading && !parentPermission) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center no-posts-container">
        <img src={LocalImages.noPostFound} alt="eyengage-empty" className="no-posts-image" />
        <p className="mg-b-0 fs-14 text-black fw-semibold mt-2">
          Please get in touch with your childcare provider to be added as a member of this community
        </p>
      </div>
    )
  }

  return (
    <>
      {postsLoading ? (
        <FeedListLoader />
      ) : <div>
        {eyengagePosts && eyengagePosts.length ?
          eyengagePosts.map((item, index) => (
            // post-item class is used to trigger lazy loading.
            <div key={`${item.id}-${index}`} className="post-item mx-n-20 pd-x-20 pd-y-15 ey-engage-item">
              <PostContainer eyengagePost={item} />
            </div>
          ))
          : <div className="d-flex flex-column align-items-center justify-content-center no-posts-container">
            <img src={LocalImages.noPostFound} alt="eyengage-empty" className="no-posts-image" />
            <p className="mg-b-0 fs-14 text-black fw-semibold mt-2">No posts/polls found</p>
          </div>
        }
      </div>}
    </>
  )
}

const FeedListLoader = () => {
  return (
    <>
      <BaseCard customBaseCardStyle="mg-t-15">
        <MediaFiveLoader />
      </BaseCard>
      <BaseCard customBaseCardStyle="mg-t-15">
        <MediaThreeLoader />
      </BaseCard>
      <BaseCard customBaseCardStyle="mg-t-15">
        <MediaTwoLoader />
      </BaseCard>
      <BaseCard customBaseCardStyle="mg-t-15">
        <MediaOneLoader />
      </BaseCard>
    </>
  )
}

export default FeedContainer;
